import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CaretDown, CaretLeft, CaretRight } from '@phosphor-icons/react';

import type { Activity } from '@/api/activities';
import type {
  AvailabilityException,
  AvailabilityResponse,
  AvailabilityRule,
  Location,
} from '@/api/locations';
import { Button } from '@/components/ui/button';
import { resolve as resolveTeamColor } from '@/lib/teamColor';
import { cn } from '@/lib/utils';

// ResourcesView — Gantt-style schedule grouped by location and zone.
//
// Layout: locations + their zones run down the left as rows; hours run
// across the top. Activity blocks position absolutely inside their
// (resource × time) cell. The label column is sticky so it stays visible
// while horizontally scrolling on narrow screens.
//
// Day-at-a-time: the screenshot Daniel referenced shows a single day, so
// we render one day per page-load and add a 7-pill day picker (Mon..Sun
// of the cursor's week) above the grid. Activities are pre-filtered to
// the week range by the parent — we filter again to the selected day
// here, no extra network round-trip.
//
// Phase C / v1 deliberately ships viz-only: no drag/drop/resize. Click
// on an activity card opens the existing detail modal via onActivityClick.

interface Props {
  // Monday of the week the user is currently viewing. Drives both the
  // day-pill picker (renders Mon-Sun of this week) and the default
  // selected day (today if it falls inside this week, else Monday).
  weekStart: Date;
  // Week's worth of activities — the parent already loads this for the
  // FullCalendar views and passes it through.
  activities: Activity[];
  locations: Location[];
  // Per-location availability (rules + exceptions). Missing entries are
  // treated as "no rules" — the row renders fully open. The parent fans
  // out N parallel queries while in resources view.
  availabilityByLocation?: Map<string, AvailabilityResponse>;
  onActivityClick: (id: string) => void;
}

// Visible hour range — full day. The resources view is the "is this
// resource booked?" lens, and that question matters at all hours (early
// training, late tournaments). Horizontal scroll handles narrow viewports.
const HOUR_START = 0;
const HOUR_END = 24;
const HOUR_COUNT = HOUR_END - HOUR_START; // 24 columns

const ROW_HEIGHT = 56; // px; tall enough for a two-line activity card
const LABEL_COL = 220; // px; left column width

// Weekday labels are i18n'd via shared keys.
const WEEKDAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;

export function ResourcesView({
  weekStart,
  activities,
  locations,
  availabilityByLocation,
  onActivityClick,
}: Props) {
  const { t, i18n } = useTranslation('common');

  // Day index within the week (0 = Monday). Default to today if today
  // is in the visible week, otherwise Monday.
  const defaultDayIdx = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const start = new Date(weekStart);
    start.setHours(0, 0, 0, 0);
    const diff = Math.floor((today.getTime() - start.getTime()) / 86_400_000);
    return diff >= 0 && diff <= 6 ? diff : 0;
  }, [weekStart]);
  const [dayIdx, setDayIdx] = useState(defaultDayIdx);

  // Per-location collapse state. Collapsed locations hide their zone
  // rows; zone-specific activities are hidden until the user expands.
  // Whole-location activities still render on the location header row.
  // Default to "everything collapsed" — the resources view is dense
  // enough that the zone-by-zone breakdown is opt-in. We initialize once
  // when locations first arrive so the user's later expand/collapse
  // choices are preserved across location refetches.
  const [collapsedLocationIds, setCollapsedLocationIds] = useState<Set<string>>(
    () => new Set(),
  );
  const collapseInitializedRef = useRef(false);
  useEffect(() => {
    if (collapseInitializedRef.current) return;
    if (locations.length === 0) return;
    setCollapsedLocationIds(new Set(locations.map((l) => l.id)));
    collapseInitializedRef.current = true;
  }, [locations]);
  const toggleCollapse = (locId: string) => {
    setCollapsedLocationIds((prev) => {
      const next = new Set(prev);
      if (next.has(locId)) next.delete(locId);
      else next.add(locId);
      return next;
    });
  };

  const dayStart = useMemo(() => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + dayIdx);
    d.setHours(0, 0, 0, 0);
    return d;
  }, [weekStart, dayIdx]);
  const dayEnd = useMemo(() => {
    const d = new Date(dayStart);
    d.setDate(d.getDate() + 1);
    return d;
  }, [dayStart]);

  // Day's activities, filtered by overlap with [dayStart, dayEnd).
  // Activities that span midnight are clipped to the day window on
  // render; this filter just decides inclusion.
  const dayActivities = useMemo(() => {
    return activities.filter((a) => {
      const start = new Date(a.starts_at).getTime();
      const end = new Date(a.ends_at).getTime();
      return start < dayEnd.getTime() && end > dayStart.getTime();
    });
  }, [activities, dayStart, dayEnd]);

  // Row layout: for each location, one location-header row (holds
  // zoneless activities) followed by one row per zone. Locations with
  // no zones still get the header row — that's where every activity
  // for that location lands.
  type Row =
    | { kind: 'location'; locationId: string; label: string }
    | { kind: 'zone'; locationId: string; zoneId: string; label: string };

  const rows: Row[] = useMemo(() => {
    const out: Row[] = [];
    for (const loc of locations) {
      out.push({ kind: 'location', locationId: loc.id, label: loc.name });
      if (collapsedLocationIds.has(loc.id)) continue;
      const zones = [...loc.zones].sort((a, b) => a.sort_order - b.sort_order);
      for (const z of zones) {
        out.push({
          kind: 'zone',
          locationId: loc.id,
          zoneId: z.id,
          label: z.name,
        });
      }
    }
    return out;
  }, [locations, collapsedLocationIds]);

  // Map (locationId, zoneId|null) → row index for O(1) placement lookup.
  const rowIndexByResource = useMemo(() => {
    const m = new Map<string, number>();
    rows.forEach((row, idx) => {
      const key =
        row.kind === 'zone'
          ? `${row.locationId}:${row.zoneId}`
          : `${row.locationId}:_`;
      m.set(key, idx);
    });
    return m;
  }, [rows]);

  // Format the day header (e.g. "Måndag 18 maj") using the current
  // browser locale + i18n locale. Falls back to en-US weekday names if
  // resolvedLanguage is missing.
  const dayLabel = useMemo(() => {
    const fmt = new Intl.DateTimeFormat(i18n.resolvedLanguage ?? 'en', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    });
    return capitaliseFirst(fmt.format(dayStart));
  }, [dayStart, i18n.resolvedLanguage]);

  // Cards: convert each day-activity into absolute-positioned block data.
  // Activities with no resolvable row (location_id missing) are skipped;
  // they show up in the regular calendar views — putting them in the
  // resources grid would be misleading.
  type Card = {
    // Unique per rendered card; activityId alone collides when a
    // whole-location activity replicates across N zone rows, and
    // React's reconciler then carries stale `top` styles between
    // collapse states. Compose with rowIdx to make it unique.
    key: string;
    activityId: string;
    rowIdx: number;
    leftPct: number;
    widthPct: number;
    title: string;
    bg: string;
    border: string;
    fg: string;
    teamNames: string;
  };
  // Quick lookup: locationId → its zone ids, in sort order. Used to
  // replicate whole-location activities (no zone_id) onto every zone row
  // so empty cells don't read as "available slots".
  const zonesByLocation = useMemo(() => {
    const m = new Map<string, string[]>();
    for (const loc of locations) {
      const ids = [...loc.zones]
        .sort((a, b) => a.sort_order - b.sort_order)
        .map((z) => z.id);
      m.set(loc.id, ids);
    }
    return m;
  }, [locations]);

  const cards: Card[] = useMemo(() => {
    const dayStartMs = dayStart.getTime();
    const dayEndMs = dayEnd.getTime();
    const windowStartMs = dayStartMs + HOUR_START * 3_600_000;
    const windowEndMs = dayStartMs + HOUR_END * 3_600_000;
    const windowSpanMs = windowEndMs - windowStartMs;

    const out: Card[] = [];
    for (const a of dayActivities) {
      if (!a.location_id) continue;

      // Clip to today and to the visible hour window.
      const startMs = Math.max(new Date(a.starts_at).getTime(), dayStartMs, windowStartMs);
      const endMs = Math.min(new Date(a.ends_at).getTime(), dayEndMs, windowEndMs);
      if (endMs <= startMs) continue;

      const leftPct = ((startMs - windowStartMs) / windowSpanMs) * 100;
      const widthPct = ((endMs - startMs) / windowSpanMs) * 100;

      const team = a.teams?.[0];
      const palette = team ? resolveTeamColor(team.name, team.color) : null;
      const teamNames = (a.teams ?? [])
        .map((t) => t.name)
        .filter(Boolean)
        .join(', ');

      // Resolve target rows. Zone-specific: one row. Whole-location: the
      // location header row + one duplicate on every zone row so empty
      // zones don't look free.
      const targetKeys: string[] = a.zone_id
        ? [`${a.location_id}:${a.zone_id}`]
        : [
            `${a.location_id}:_`,
            ...(zonesByLocation.get(a.location_id) ?? []).map(
              (zid) => `${a.location_id}:${zid}`,
            ),
          ];

      for (const key of targetKeys) {
        const rowIdx = rowIndexByResource.get(key);
        if (rowIdx === undefined) continue; // row hidden by collapse

        out.push({
          key: `${a.id}@${rowIdx}`,
          activityId: a.id,
          rowIdx,
          leftPct,
          widthPct,
          title: a.title,
          bg: palette?.dot ?? 'var(--lime)',
          border: palette?.dot ?? 'var(--lime)',
          // PALETTE_L_DOT lightness was chosen so ink-on-dot meets WCAG AA;
          // matches the convention used by other team-colored chips in the
          // app (the calendar week-view cards, the team-list dot+label rows).
          fg: 'var(--ink)',
          teamNames,
        });
      }
    }
    return out;
  }, [dayActivities, rowIndexByResource, zonesByLocation, dayStart, dayEnd]);

  // Closed-time overlays. For each row (location header or zone), figure
  // out which segments of the day fall OUTSIDE the configured open hours
  // and render them as muted bands so the user can see at a glance
  // when each resource is unavailable.
  //
  // Precedence matches the FullCalendar branch in Schedule.tsx:
  //   - zone-level rules/exceptions win when present
  //   - location-level fall back when zone has none
  // Hours-replace exceptions override the day's normal rules.
  type Band = { rowIdx: number; leftPct: number; widthPct: number };
  const closedBands: Band[] = useMemo(() => {
    if (!availabilityByLocation || availabilityByLocation.size === 0) return [];

    const dayStartMs = dayStart.getTime();
    const windowStartMs = dayStartMs + HOUR_START * 3_600_000;
    const windowEndMs = dayStartMs + HOUR_END * 3_600_000;
    const windowSpanMs = windowEndMs - windowStartMs;

    // ISO day-of-week: Mon=1..Sun=7. JS getDay: Sun=0..Sat=6.
    const isoDow = ((dayStart.getDay() + 6) % 7) + 1;
    const isoDate = formatIsoDate(dayStart);

    // hh:mm → ms offset from midnight.
    const hmToMs = (hm: string): number => {
      const [h, m] = hm.split(':').map(Number);
      return ((h ?? 0) * 60 + (m ?? 0)) * 60 * 1000;
    };

    const ruleApplies = (r: AvailabilityRule): boolean => {
      if (r.day_of_week !== isoDow) return false;
      if (r.effective_from && isoDate < r.effective_from) return false;
      if (r.effective_until && isoDate > r.effective_until) return false;
      return true;
    };

    // Compute [open) intervals within the visible window for a rule set
    // plus any hours-replace exception for the day. Returns ms-offsets
    // from dayStartMs.
    const openIntervals = (
      rules: AvailabilityRule[],
      exceptions: AvailabilityException[],
    ): Array<[number, number]> => {
      // Closed-day exception → no open intervals (caller renders one
      // band over the whole window).
      if (exceptions.some((e) => e.on_date === isoDate && e.kind === 'closed')) {
        return [];
      }
      // Hours-replace exception → replaces the day's rules with the
      // exception window. (Multiple hours exceptions on the same day are
      // unioned.)
      const hoursExc = exceptions.filter(
        (e) => e.on_date === isoDate && e.kind === 'hours' && e.start_time && e.end_time,
      );
      const source: Array<[number, number]> = hoursExc.length > 0
        ? hoursExc.map((e) => [hmToMs(e.start_time!), hmToMs(e.end_time!)])
        : rules.filter(ruleApplies).map((r) => [hmToMs(r.start_time), hmToMs(r.end_time)]);
      // Merge overlapping/adjacent intervals so we don't compute the
      // complement against a non-canonical set.
      source.sort((a, b) => a[0] - b[0]);
      const merged: Array<[number, number]> = [];
      for (const [s, e] of source) {
        const last = merged[merged.length - 1];
        if (last && s <= last[1]) {
          last[1] = Math.max(last[1], e);
        } else {
          merged.push([s, e]);
        }
      }
      return merged;
    };

    // Complement of open intervals inside the visible [windowStart, windowEnd]
    // band, expressed as percentage offsets across the window.
    const closedFor = (
      rules: AvailabilityRule[],
      exceptions: AvailabilityException[],
    ): Array<[number, number]> => {
      const open = openIntervals(rules, exceptions);
      const windowStartOffset = HOUR_START * 3_600_000;
      const windowEndOffset = HOUR_END * 3_600_000;
      // If no rules apply, the resource has no published open hours for
      // this day — we treat that as "no shading" rather than "all
      // closed" so a location without any rules configured doesn't
      // visually scream every day.
      if (open.length === 0 && rules.filter(ruleApplies).length === 0
          && !exceptions.some((e) => e.on_date === isoDate)) {
        return [];
      }
      const out: Array<[number, number]> = [];
      let cursor = windowStartOffset;
      for (const [s, e] of open) {
        if (e <= windowStartOffset || s >= windowEndOffset) continue;
        const clipS = Math.max(s, windowStartOffset);
        const clipE = Math.min(e, windowEndOffset);
        if (cursor < clipS) out.push([cursor, clipS]);
        cursor = Math.max(cursor, clipE);
      }
      if (cursor < windowEndOffset) out.push([cursor, windowEndOffset]);
      return out.map(([s, e]) => [
        ((s - windowStartOffset) / windowSpanMs) * 100,
        ((e - s) / windowSpanMs) * 100,
      ]);
    };

    const bands: Band[] = [];
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i]!;
      const av = availabilityByLocation.get(row.locationId);
      if (!av) continue;
      const locRules = av.rules[''] ?? [];
      const locExc = av.exceptions[''] ?? [];
      const [rules, exceptions] =
        row.kind === 'zone'
          ? [
              av.rules[row.zoneId]?.length ? av.rules[row.zoneId]! : locRules,
              av.exceptions[row.zoneId]?.length ? av.exceptions[row.zoneId]! : locExc,
            ]
          : [locRules, locExc];
      for (const [leftPct, widthPct] of closedFor(rules, exceptions)) {
        bands.push({ rowIdx: i, leftPct, widthPct });
      }
    }
    return bands;
  }, [rows, availabilityByLocation, dayStart]);

  // "Now" marker — vertical line at the current time, only when the
  // selected day is today and "now" falls inside the visible hour window.
  // Recomputed once per minute via nowTick so the marker advances.
  const [nowTick, setNowTick] = useState(0);
  const isToday = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today.getTime() === dayStart.getTime();
  }, [dayStart]);
  useEffect(() => {
    if (!isToday) return;
    const id = window.setInterval(() => setNowTick((t) => t + 1), 60_000);
    return () => window.clearInterval(id);
  }, [isToday]);
  const nowLeftPct = useMemo(() => {
    if (!isToday) return null;
    const dayStartMs = dayStart.getTime();
    const windowStartMs = dayStartMs + HOUR_START * 3_600_000;
    const windowEndMs = dayStartMs + HOUR_END * 3_600_000;
    const nowMs = Date.now();
    if (nowMs < windowStartMs || nowMs > windowEndMs) return null;
    return ((nowMs - windowStartMs) / (windowEndMs - windowStartMs)) * 100;
    // nowTick included so the marker advances each minute.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isToday, dayStart, nowTick]);

  // Scroll the grid so the "now" line lands near the centre when the
  // user lands on today, or switches days back to today. Runs once per
  // (day = today) transition, not every minute — re-scrolling on every
  // tick would fight the user's manual horizontal scroll.
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (nowLeftPct === null) return;
    const container = scrollContainerRef.current;
    if (!container) return;
    const innerHourArea = container.scrollWidth - LABEL_COL;
    if (innerHourArea <= 0) return;
    const markerX = LABEL_COL + innerHourArea * (nowLeftPct / 100);
    const targetScroll = markerX - container.clientWidth / 2;
    container.scrollTo({
      left: Math.max(0, targetScroll),
      behavior: 'smooth',
    });
    // Intentionally only fires on isToday transitions, not on nowTick.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isToday, rows.length]);

  if (locations.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-border bg-card p-8 text-center text-sm text-foreground/60">
        {t('schedule.view.resourcesEmpty')}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {/* Day picker — pill row of Mon..Sun + a small label of the chosen
          day. Clicking a pill switches the grid; the existing
          week-prev/next buttons stay in the parent toolbar. */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <span className="font-heading text-sm font-semibold">{dayLabel}</span>
        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="icon"
            aria-label={t('common.back')}
            onClick={() => setDayIdx((i) => (i === 0 ? 6 : i - 1))}
          >
            <CaretLeft weight="bold" />
          </Button>
          <div
            role="tablist"
            aria-label={t('schedule.view.resourcesDayPicker')}
            className="flex items-center gap-1 rounded-pill bg-muted p-1"
          >
            {WEEKDAY_KEYS.map((key, idx) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={dayIdx === idx}
                onClick={() => setDayIdx(idx)}
                className={cn(
                  'rounded-pill px-3 py-1 text-xs font-semibold transition-colors',
                  dayIdx === idx
                    ? 'bg-card shadow-xs'
                    : 'text-foreground/70 hover:text-foreground',
                )}
              >
                {t(`rules.weekdayShort.${key}`)}
              </button>
            ))}
          </div>
          <Button
            variant="outline"
            size="icon"
            aria-label={t('common.continue')}
            onClick={() => setDayIdx((i) => (i === 6 ? 0 : i + 1))}
          >
            <CaretRight weight="bold" />
          </Button>
        </div>
      </div>

      {/* Grid — outer wrapper handles horizontal scroll on narrow
          viewports. The inner element has a fixed min-width so columns
          stay wide enough to be useful on mobile (~52px per hour ≈ 884px
          total + 220px label column). */}
      <div
        ref={scrollContainerRef}
        className="overflow-x-auto rounded-2xl border border-border bg-card"
      >
        <div className="relative" style={{ minWidth: LABEL_COL + HOUR_COUNT * 52 }}>
          {/* Hour header row */}
          <div
            className="sticky top-0 z-10 grid border-b border-border bg-card"
            style={{
              gridTemplateColumns: `${LABEL_COL}px repeat(${HOUR_COUNT}, 1fr)`,
            }}
          >
            <div className="px-3 py-2 text-xs font-semibold text-foreground/60">
              {t('schedule.view.resourcesLabelHeader')}
            </div>
            {Array.from({ length: HOUR_COUNT }, (_, i) => HOUR_START + i).map((h) => (
              <div
                key={h}
                className="border-l border-border px-2 py-2 text-xs font-medium text-foreground/60"
              >
                {String(h).padStart(2, '0')}:00
              </div>
            ))}
          </div>

          {/* Body rows — explicit height + overflow:hidden so absolutely-
              positioned activity cards can never paint past the visible
              rows (e.g. if a card ends up with a stale rowIdx after a
              collapse). */}
          <div
            className="relative overflow-hidden"
            style={{ height: rows.length * ROW_HEIGHT }}
          >
            {rows.map((row) => (
              <div
                key={
                  row.kind === 'zone'
                    ? `${row.locationId}:${row.zoneId}`
                    : `${row.locationId}:loc`
                }
                className={cn(
                  'grid border-b border-border last:border-b-0',
                  row.kind === 'location' ? 'bg-muted' : 'bg-card',
                )}
                style={{
                  gridTemplateColumns: `${LABEL_COL}px repeat(${HOUR_COUNT}, 1fr)`,
                  height: ROW_HEIGHT,
                }}
              >
                {/* Sticky label cell — needs a solid bg + z-index above the
                    absolutely-positioned activity cards so cards from early
                    hours don't bleed through it when the user scrolls right. */}
                <div
                  className={cn(
                    'sticky left-0 z-[2] flex items-center gap-1 px-3 text-sm',
                    row.kind === 'location'
                      ? 'bg-muted font-heading font-semibold'
                      : 'bg-card text-foreground/80 pl-7',
                  )}
                >
                  {row.kind === 'location' ? (
                    <button
                      type="button"
                      onClick={() => toggleCollapse(row.locationId)}
                      aria-expanded={!collapsedLocationIds.has(row.locationId)}
                      aria-label={
                        collapsedLocationIds.has(row.locationId)
                          ? t('schedule.view.resourcesExpand', { name: row.label })
                          : t('schedule.view.resourcesCollapse', { name: row.label })
                      }
                      className="shrink-0 rounded-md p-1 text-foreground/60 hover:bg-foreground/5 hover:text-foreground"
                    >
                      <CaretDown
                        weight="bold"
                        size={12}
                        className={cn(
                          'transition-transform duration-200',
                          collapsedLocationIds.has(row.locationId) && '-rotate-90',
                        )}
                      />
                    </button>
                  ) : null}
                  <span className="truncate">{row.label}</span>
                </div>
                {/* Hour cells — purely visual gridlines; activity cards
                    overlay this layer absolutely positioned. */}
                {Array.from({ length: HOUR_COUNT }, (_, i) => (
                  <div key={i} className="border-l border-border/60" />
                ))}
              </div>
            ))}

            {/* Closed-time overlay — muted bands behind activity cards
                marking when each resource is unavailable per its
                opening-hours rules. Rendered before cards so the cards
                always sit on top. */}
            {closedBands.map((b, i) => (
              <div
                key={`closed-${b.rowIdx}-${i}`}
                aria-hidden
                className="pointer-events-none absolute bg-foreground/[0.06]"
                style={{
                  top: b.rowIdx * ROW_HEIGHT,
                  left: `calc(${LABEL_COL}px + (100% - ${LABEL_COL}px) * ${b.leftPct / 100})`,
                  width: `calc((100% - ${LABEL_COL}px) * ${b.widthPct / 100})`,
                  height: ROW_HEIGHT,
                }}
              />
            ))}

            {/* Activity overlay — each card positions itself absolutely
                inside its row. We use `top = rowIdx * ROW_HEIGHT` so the
                stack matches the grid above. */}
            {cards.map((c) => (
              <button
                key={c.key}
                type="button"
                onClick={() => onActivityClick(c.activityId)}
                className="absolute flex flex-col items-start justify-center overflow-hidden rounded-lg border px-2 py-1 text-left text-xs shadow-xs transition-transform hover:scale-[1.02] motion-reduce:hover:scale-100"
                style={{
                  top: c.rowIdx * ROW_HEIGHT + 4,
                  left: `calc(${LABEL_COL}px + (100% - ${LABEL_COL}px) * ${c.leftPct / 100})`,
                  width: `calc((100% - ${LABEL_COL}px) * ${c.widthPct / 100})`,
                  height: ROW_HEIGHT - 8,
                  background: c.bg,
                  borderColor: c.border,
                  color: c.fg,
                }}
              >
                <span className="truncate font-semibold leading-tight">{c.title}</span>
                {c.teamNames && (
                  <span className="truncate text-[0.65rem] opacity-80">{c.teamNames}</span>
                )}
              </button>
            ))}

            {/* "Now" marker — thin vertical line at the current time when
                viewing today, with a dot at the top so it reads as a
                marker not a divider. */}
            {nowLeftPct !== null && (
              <div
                aria-hidden
                className="pointer-events-none absolute top-0 z-[3] w-px bg-primary"
                style={{
                  left: `calc(${LABEL_COL}px + (100% - ${LABEL_COL}px) * ${nowLeftPct / 100})`,
                  height: rows.length * ROW_HEIGHT,
                }}
              >
                <div className="absolute -left-[3px] -top-[3px] size-1.5 rounded-full bg-primary" />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function capitaliseFirst(s: string): string {
  if (!s) return s;
  return s[0]!.toUpperCase() + s.slice(1);
}

// formatIsoDate emits YYYY-MM-DD in the local zone — matching the format
// the availability API uses for `on_date` and `effective_from/until`.
function formatIsoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
